import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  private cmsUrl = environment.cmsUrl;

  constructor(
    private http: HttpClient,
    private translationService: TranslationService
  ) {}

  /**
   * Checks if product data should be loaded from CMS or if fallback data should be used
   * @returns Observable that errors if blogOnly is enabled
   */
  private checkProductDataSource(): Observable<boolean> {
    if (environment.blogOnly) {
      console.log('Using fallback product data (blogOnly mode enabled)');
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    return of(true);
  }

  /**
   * Fetches all product categories with their products
   * @returns Observable with product categories data
   */
  public getProductCategories(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/product-categories?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches home page data from the CMS
   * @returns Observable with home page data
   */
  public getHomePage(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/home?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all home models
   * @returns Observable with home models data
   */
  public getHomeModels(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/home-models?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific home model by slug
   * @param slug The slug of the home model
   * @returns Observable with home model data
   */
  public getHomeModelBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/home-models?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all mobile home products
   * @returns Observable with mobile homes data
   */
  public getMobileHomes(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/mobile-homes?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific mobile home by slug
   * @param slug The slug of the mobile home
   * @returns Observable with mobile home data
   */
  public getMobileHomeBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/mobile-homes?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all garage products
   * @returns Observable with garages data
   */
  public getGarages(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/garages?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific garage product by slug
   * @param slug The slug of the garage product
   * @returns Observable with garage product data
   */
  public getGarageBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/garages?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all carport products
   * @returns Observable with carports data
   */
  public getCarports(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/carports?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific carport product by slug
   * @param slug The slug of the carport product
   * @returns Observable with carport product data
   */
  public getCarportBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/carports?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all container products
   * @returns Observable with containers data
   */
  public getContainers(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/containers?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific container product by slug
   * @param slug The slug of the container product
   * @returns Observable with container product data
   */
  public getContainerBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/containers?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all outdoor kitchen products
   * @returns Observable with outdoor kitchens data
   */
  public getOutdoorKitchens(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/outdoor-kitchens?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific outdoor kitchen product by slug
   * @param slug The slug of the outdoor kitchen product
   * @returns Observable with outdoor kitchen product data
   */
  public getOutdoorKitchenBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/outdoor-kitchens?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all garbage bin products
   * @returns Observable with garbage bins data
   */
  public getGarbageBins(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/garbage-bins?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific garbage bin product by slug
   * @param slug The slug of the garbage bin product
   * @returns Observable with garbage bin product data
   */
  public getGarbageBinBySlug(slug: string): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/garbage-bins?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches all home & garden products
   * @returns Observable with home & garden data
   */
  public getHomeAndGarden(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/home-garden?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches about us page data
   * @returns Observable with about us data
   */
  public getAboutUs(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/about?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches contact page information
   * @returns Observable with contact information
   */
  public getContactInfo(): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/contact?locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches blog posts with pagination
   * @param page Page number
   * @param pageSize Number of posts per page
   * @returns Observable with blog posts data
   */
  public getBlogPosts(
    page: number = 1,
    pageSize: number = 10
  ): Observable<any> {
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(
        `${this.cmsUrl}/api/blog-posts?locale=${lang}&pagination[page]=${page}&pagination[pageSize]=${pageSize}`
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Fetches a specific blog post by slug
   * @param slug The slug of the blog post
   * @returns Observable with blog post data
   */
  public getBlogPostBySlug(slug: string): Observable<any> {
    const lang = this.translationService.getCurrentLanguage();
    return this.http
      .get(`${this.cmsUrl}/api/blog-posts?slug=${slug}&locale=${lang}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Submits an enquiry form
   * @param formData The form data
   * @returns Observable with submission result
   */
  public submitEnquiry(formData: any): Observable<any> {
    // If blogOnly is enabled, return an error to trigger fallback data
    if (environment.blogOnly) {
      return throwError(() => new Error('blogOnly mode enabled'));
    }
    
    return this.http
      .post(`${this.cmsUrl}/api/enquiries`, { data: formData })
      .pipe(catchError(this.handleError));
  }

  /**
   * Helper method to handle API errors
   * @param error The error object
   * @returns An observable that emits the error
   */
  private handleError(error: any): Observable<never> {
    console.error('API error:', error);
    let errorMessage = 'An unknown error occurred';

    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  }
}
